import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { uniqueId } from 'lodash-es';
import { forwardRef, useMemo } from 'react';
import { renderWithTooltip } from '../Tooltip';
import { LabelWithHint } from '../Typography/LabelWithHint';
import { renderRequiredLabel } from './Label';
import { StyledLabel, StyledHelpText, StyledLabelWithHint } from './styled';
import { TextAreaRoot, StyledTextAreaContainer, StyledTextArea } from './TextArea.styled';
import { tokens } from './tokens';
import { InputVariants, ResizeDirection } from './types';
import { TriggerKind, useInput } from './useInput';
export const TextArea = forwardRef(({ label, required = false, helpText, maxLength, className, style, disabled, variant = InputVariants.primary, value, resize = ResizeDirection.vertical, onChange, placeholder, placeholderSuggestion, delay = 250, hint, tooltip, id, onBlur, onFocus, autoGrow = false, rows, maxRows = Number.MAX_SAFE_INTEGER, renderTextArea = (node) => node, ...restProps }, ref) => {
    const textAreaId = useMemo(() => id || uniqueId('textArea'), [id]);
    const { _value, valueLength, handleChange, handleBlur } = useInput({
        value,
        onChange,
        delay,
        onBlur
    });
    const _onFocus = (e) => {
        if (placeholderSuggestion) {
            e.currentTarget.value = placeholderSuggestion;
            e.currentTarget.select();
            handleChange(e, TriggerKind.Auto);
        }
        onFocus?.(e);
    };
    const labelUI = label ? _jsx(StyledLabel, { htmlFor: textAreaId, children: label }) : null;
    const labelUIWrapper = renderRequiredLabel({ label: labelUI, required });
    const _rows = autoGrow ? Math.min(maxRows, (_value || '').toString().split('\n').length) : rows;
    let textAreadUI = renderTextArea(_jsx(StyledTextAreaContainer, { variant: variant, disabled: disabled, "data-placeholder": placeholder, "data-value": _value, children: _jsx(StyledTextArea, { ref: ref, id: textAreaId, disabled: disabled, value: _value, rows: _rows, resize: resize, onChange: (e) => {
                if (autoGrow) {
                    const currentTarget = e.currentTarget;
                    // Note: We manually set the rows to avoid height changing after the rerender
                    const rows = Math.min(maxRows, e.currentTarget.value.split('\n').length);
                    currentTarget.rows = rows;
                }
                handleChange(e, TriggerKind.User);
            }, onBlur: handleBlur, onFocus: _onFocus, placeholder: placeholderSuggestion || placeholder, "data-placeholder": placeholder, "data-value": _value, maxLength: maxLength, ...restProps }) }));
    if (tooltip) {
        textAreadUI = _jsx(_Fragment, { children: renderWithTooltip(textAreadUI, tooltip) });
    }
    if (!label && hint) {
        textAreadUI = _jsx(LabelWithHint, { hint: hint, children: textAreadUI });
    }
    return (_jsxs(TextAreaRoot, { style: style, className: className, gap: tokens.Root.gap, alignY: 'stretch', children: [hint && label ? (_jsx(StyledLabelWithHint, { level: 'xs', hint: hint, children: labelUIWrapper })) : (labelUIWrapper), textAreadUI, helpText && (_jsxs(StyledHelpText, { variant: variant, direction: 'horizontal', align: 'space-between', children: [helpText, maxLength && maxLength > 0 ? _jsx("span", { children: valueLength + '/' + maxLength }) : null] }))] }));
});
